import { useState, useEffect, useCallback, useRef } from 'react'
import {
  MessageSquare,
  Search,
  Loader2,
  Send,
  AlertCircle,
  Clock,
  CheckCircle2,
  ArrowUpCircle,
  XCircle,
  User,
  Mail,
  Phone,
  Shield,
  Calendar,
  RefreshCw,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { SupportConversation, SupportStatus, SupportCategory, SupportStats, ChatMessage, SupportUserProfile } from '../types'
import {
  fetchSupportStats,
  fetchSupportConversations,
  fetchSupportConversation,
  sendSupportReply,
  updateSupportStatus,
  markSupportRead,
} from '../services/adminService'

// ── Status styling ─────────────────────────────────────────────────
const STATUS_CONFIG: Record<SupportStatus, { bg: string; text: string; dot: string; label: string; icon: React.ElementType }> = {
  OPEN: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Open', icon: AlertCircle },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500', label: 'In Progress', icon: Clock },
  RESOLVED: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', label: 'Resolved', icon: CheckCircle2 },
  ESCALATED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', label: 'Escalated', icon: ArrowUpCircle },
  CLOSED: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-500', label: 'Closed', icon: XCircle },
}

const CATEGORY_CONFIG: Record<SupportCategory, { bg: string; text: string; label: string }> = {
  PAYMENT_ISSUE: { bg: 'bg-purple-50', text: 'text-purple-600', label: 'Payment' },
  PICKUP_ISSUE: { bg: 'bg-orange-50', text: 'text-orange-600', label: 'Pickup' },
  ACCOUNT_ISSUE: { bg: 'bg-cyan-50', text: 'text-cyan-600', label: 'Account' },
  OTHER: { bg: 'bg-gray-50', text: 'text-gray-600', label: 'Other' },
}

const STATUS_FILTERS: { value: SupportStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ESCALATED', label: 'Escalated' },
]

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function formatTimestamp(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function Support() {
  // ── List state ───────────────────────────────────────────────────
  const [stats, setStats] = useState<SupportStats>({ open: 0, inProgress: 0, resolved: 0, escalated: 0, total: 0 })
  const [conversations, setConversations] = useState<SupportConversation[]>([])
  const [listTotal, setListTotal] = useState(0)
  const [listLoading, setListLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<SupportStatus | 'ALL'>('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  // ── Detail state ─────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeConversation, setActiveConversation] = useState<SupportConversation | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [userProfile, setUserProfile] = useState<SupportUserProfile | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── Load stats ───────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const data = await fetchSupportStats()
      setStats(data ?? { open: 0, inProgress: 0, resolved: 0, escalated: 0, total: 0 })
    } catch {
      // Stats are non-critical
    }
  }, [])

  // ── Load conversation list ───────────────────────────────────────
  const loadConversations = useCallback(async () => {
    setListLoading(true)
    try {
      const data = await fetchSupportConversations({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        search: searchQuery || undefined,
        limit: 50,
      })
      setConversations(data?.conversations ?? [])
      // The API sends the count under pagination.total.
      setListTotal(data?.pagination?.total ?? data?.total ?? 0)
    } catch {
      toast.error('Failed to load support conversations')
    } finally {
      setListLoading(false)
    }
  }, [statusFilter, searchQuery])

  useEffect(() => {
    loadStats()
    loadConversations()
  }, [loadStats, loadConversations])

  // ── Load single conversation detail ──────────────────────────────
  const loadConversationDetail = useCallback(async (id: string, silent = false) => {
    if (!silent) setDetailLoading(true)
    try {
      const data = await fetchSupportConversation(id)
      setActiveConversation(data?.conversation ?? null)
      setMessages(data?.messages ?? [])
      setUserProfile(data?.userProfile ?? data?.conversation?.user ?? null)
      markSupportRead(id).catch(() => {})
    } catch {
      if (!silent) toast.error('Failed to load conversation')
    } finally {
      setDetailLoading(false)
    }
  }, [])

  // ── Select conversation ──────────────────────────────────────────
  const handleSelect = useCallback((id: string) => {
    setSelectedId(id)
    setReplyText('')
    loadConversationDetail(id)
  }, [loadConversationDetail])

  // ── Auto-refresh messages every 15s for active conversation ──────
  useEffect(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current)
    if (!selectedId) return

    refreshTimerRef.current = setInterval(() => {
      loadConversationDetail(selectedId, true)
    }, 15000)

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current)
    }
  }, [selectedId, loadConversationDetail])

  // ── Scroll to bottom when messages change ────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ── Send reply ───────────────────────────────────────────────────
  const handleSendReply = async () => {
    if (!selectedId || !replyText.trim() || sending) return
    setSending(true)
    try {
      await sendSupportReply(selectedId, replyText.trim())
      setReplyText('')
      await loadConversationDetail(selectedId, true)
      toast.success('Reply sent')
    } catch {
      toast.error('Failed to send reply')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendReply()
    }
  }

  // ── Update conversation status ───────────────────────────────────
  const handleStatusUpdate = async (newStatus: string) => {
    if (!selectedId || updatingStatus) return
    setUpdatingStatus(true)
    try {
      const updated = await updateSupportStatus(selectedId, newStatus)
      setActiveConversation(updated)
      loadConversations()
      loadStats()
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus as SupportStatus]?.label ?? newStatus}`)
    } catch {
      toast.error('Failed to update status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  // ── Stat cards config ────────────────────────────────────────────
  const statCards = [
    { label: 'Open', value: stats.open, color: 'bg-amber-50 border-amber-100', textColor: 'text-amber-600', valColor: 'text-amber-700', icon: AlertCircle },
    { label: 'In Progress', value: stats.inProgress, color: 'bg-blue-50 border-blue-100', textColor: 'text-blue-600', valColor: 'text-blue-700', icon: Clock },
    { label: 'Resolved', value: stats.resolved, color: 'bg-green-50 border-green-100', textColor: 'text-green-600', valColor: 'text-green-700', icon: CheckCircle2 },
    { label: 'Escalated', value: stats.escalated, color: 'bg-red-50 border-red-100', textColor: 'text-red-600', valColor: 'text-red-700', icon: ArrowUpCircle },
  ]

  return (
    <div className="flex gap-5 h-[calc(100vh-8rem)]">
      {/* ── LEFT PANEL: Conversation List ──────────────────────────── */}
      <div className="w-[400px] flex-shrink-0 flex flex-col gap-4">
        {/* Stats row */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
          {statCards.map(({ label, value, color, textColor, valColor, icon: Icon }) => (
            <div key={label} className={`rounded-2xl p-3 border ${color} text-center`}>
              <Icon className={`w-4 h-4 mx-auto mb-1 ${textColor}`} />
              <p className={`text-lg font-bold ${valColor}`}>{value}</p>
              <p className={`text-[10px] font-medium ${textColor}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {STATUS_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setStatusFilter(value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                  statusFilter === value
                    ? 'bg-primary-600 text-white'
                    : 'bg-[#F8FAF9] text-[#6B7280] hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-[#6B7280]" />
            <input
              type="text"
              placeholder="Search by reference or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {!listLoading && listTotal > conversations.length && (
            <p className="px-4 py-2 text-[11px] text-[#6B7280]">
              Showing the {conversations.length} most recent of {listTotal}. Narrow with the status filter or search.
            </p>
          )}
          {listLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
              <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-[#6B7280]">No conversations found</p>
              <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            conversations.map((conv) => {
              const statusCfg = STATUS_CONFIG[conv.status] ?? STATUS_CONFIG.OPEN
              const catCfg = CATEGORY_CONFIG[conv.category] ?? CATEGORY_CONFIG.OTHER
              const isActive = selectedId === conv.id

              return (
                <button
                  key={conv.id}
                  onClick={() => handleSelect(conv.id)}
                  className={`w-full text-left bg-white rounded-2xl p-4 shadow-sm border transition-all duration-150 ${
                    isActive
                      ? 'border-primary-600 ring-2 ring-primary-100 shadow-md'
                      : 'border-gray-100 hover:shadow-md hover:border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${statusCfg.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-bold text-[#1A1A2E] font-mono">
                          {conv.reference}
                        </span>
                        <span className="text-[10px] text-[#6B7280] whitespace-nowrap">
                          {timeAgo(conv.lastMsgAt || conv.createdAt)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-[#1A1A2E] truncate">
                          {conv.user?.name ?? 'Unknown User'}
                        </span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${catCfg.bg} ${catCfg.text}`}>
                          {catCfg.label}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-[#1A1A2E] truncate mb-0.5">
                        {conv.subject}
                      </p>
                      {conv.lastMessage && (
                        <p className="text-[11px] text-[#6B7280] truncate">
                          {conv.lastMessage.content}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusCfg.bg} ${statusCfg.text}`}>
                          {statusCfg.label}
                        </span>
                        {(conv.unreadCount ?? 0) > 0 && (
                          <span className="text-[10px] font-bold text-white bg-primary-600 px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })
          )}
        </div>

        {/* Refresh button */}
        <button
          onClick={() => { loadConversations(); loadStats() }}
          className="flex items-center justify-center gap-2 py-2.5 bg-white rounded-xl border border-gray-200 text-xs font-semibold text-[#6B7280] hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* ── RIGHT PANEL: Conversation Detail ───────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#6B7280]">Select a conversation to view details</p>
              <p className="text-xs text-gray-400 mt-1">Choose from the list on the left</p>
            </div>
          </div>
        ) : detailLoading ? (
          <div className="flex-1 flex items-center justify-center bg-white rounded-2xl shadow-sm border border-gray-100">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : activeConversation ? (
          <div className="flex-1 flex gap-4 min-h-0">
            {/* Messages area */}
            <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#1A1A2E] font-mono">
                      {activeConversation.reference}
                    </span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CONFIG[activeConversation.status]?.bg} ${STATUS_CONFIG[activeConversation.status]?.text}`}>
                      {STATUS_CONFIG[activeConversation.status]?.label}
                    </span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_CONFIG[activeConversation.category]?.bg ?? 'bg-gray-50'} ${CATEGORY_CONFIG[activeConversation.category]?.text ?? 'text-gray-600'}`}>
                      {CATEGORY_CONFIG[activeConversation.category]?.label ?? 'Other'}
                    </span>
                  </div>
                  <span className="text-xs text-[#6B7280]">
                    {userProfile?.name ?? activeConversation.user?.name ?? 'Unknown User'}
                  </span>
                </div>
                <p className="text-xs text-[#6B7280] mb-3">{activeConversation.subject}</p>

                {/* Action buttons */}
                <div className="flex gap-2">
                  {activeConversation.status === 'OPEN' && (
                    <button
                      onClick={() => handleStatusUpdate('IN_PROGRESS')}
                      disabled={updatingStatus}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50"
                    >
                      <Clock size={12} />
                      Mark In Progress
                    </button>
                  )}
                  {(activeConversation.status === 'OPEN' || activeConversation.status === 'IN_PROGRESS' || activeConversation.status === 'ESCALATED') && (
                    <button
                      onClick={() => handleStatusUpdate('RESOLVED')}
                      disabled={updatingStatus}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs font-semibold text-green-700 hover:bg-green-100 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle2 size={12} />
                      Resolve
                    </button>
                  )}
                  {(activeConversation.status === 'OPEN' || activeConversation.status === 'IN_PROGRESS') && (
                    <button
                      onClick={() => handleStatusUpdate('ESCALATED')}
                      disabled={updatingStatus}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-xs font-semibold text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      <ArrowUpCircle size={12} />
                      Escalate
                    </button>
                  )}
                  {activeConversation.status === 'RESOLVED' && (
                    <button
                      onClick={() => handleStatusUpdate('CLOSED')}
                      disabled={updatingStatus}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      <XCircle size={12} />
                      Close
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#F8FAF9]">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-xs text-[#6B7280]">No messages yet</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isAdmin = msg.sender?.role === 'ADMIN'
                    const isSystem = msg.isSystemMsg

                    if (isSystem) {
                      return (
                        <div key={msg.id} className="flex justify-center">
                          <p className="text-[10px] text-[#6B7280] italic bg-white/60 px-3 py-1 rounded-full">
                            {msg.content}
                          </p>
                        </div>
                      )
                    }

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[75%] ${isAdmin ? 'order-1' : ''}`}>
                          <div
                            className={`px-4 py-2.5 rounded-2xl ${
                              isAdmin
                                ? 'bg-primary-600 text-white rounded-br-md'
                                : 'bg-white text-[#1A1A2E] border border-gray-100 rounded-bl-md'
                            }`}
                          >
                            <p className="text-xs leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          <div className={`flex items-center gap-1.5 mt-1 ${isAdmin ? 'justify-end' : ''}`}>
                            <span className="text-[10px] text-[#6B7280]">
                              {msg.sender?.name ?? 'Unknown'}
                            </span>
                            <span className="text-[10px] text-[#6B7280]">
                              {formatTimestamp(msg.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply input */}
              {activeConversation.status !== 'CLOSED' && (
                <div className="px-5 py-3 border-t border-gray-100 bg-white">
                  <div className="flex items-end gap-3">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your reply... (Enter to send, Shift+Enter for new line)"
                      rows={2}
                      className="flex-1 px-4 py-2.5 bg-[#F8FAF9] border border-gray-200 rounded-xl text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none focus:border-primary-600 focus:ring-1 focus:ring-primary-100 resize-none"
                    />
                    <button
                      onClick={handleSendReply}
                      disabled={!replyText.trim() || sending}
                      className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User profile card */}
            <div className="w-60 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="text-xs font-bold text-[#1A1A2E] uppercase tracking-wider mb-4">
                  User Profile
                </h3>

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-bold">
                    {(userProfile?.name ?? activeConversation.user?.name ?? '?')
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A2E] truncate">
                      {userProfile?.name ?? activeConversation.user?.name ?? 'Unknown'}
                    </p>
                    <p className="text-[10px] text-[#6B7280] capitalize">
                      {(userProfile?.role ?? activeConversation.user?.role ?? 'user').toLowerCase()}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {(userProfile?.email ?? activeConversation.user?.email) && (
                    <div className="flex items-start gap-2.5">
                      <Mail className="w-3.5 h-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] text-[#6B7280]">Email</p>
                        <p className="text-xs font-medium text-[#1A1A2E] truncate">
                          {userProfile?.email ?? activeConversation.user?.email}
                        </p>
                      </div>
                    </div>
                  )}

                  {(userProfile?.phone ?? activeConversation.user?.phone) && (
                    <div className="flex items-start gap-2.5">
                      <Phone className="w-3.5 h-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] text-[#6B7280]">Phone</p>
                        <p className="text-xs font-medium text-[#1A1A2E]">
                          {userProfile?.phone ?? activeConversation.user?.phone}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-2.5">
                    <Shield className="w-3.5 h-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-[#6B7280]">Role</p>
                      <p className="text-xs font-medium text-[#1A1A2E] capitalize">
                        {(userProfile?.role ?? activeConversation.user?.role ?? 'User').toLowerCase()}
                      </p>
                    </div>
                  </div>

                  {userProfile?.createdAt && (
                    <div className="flex items-start gap-2.5">
                      <Calendar className="w-3.5 h-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] text-[#6B7280]">Joined</p>
                        <p className="text-xs font-medium text-[#1A1A2E]">
                          {new Date(userProfile.createdAt).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Conversation meta */}
                <div className="mt-5 pt-4 border-t border-gray-100 space-y-2.5">
                  <h4 className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider">
                    Conversation Details
                  </h4>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-[#6B7280]">Created</span>
                    <span className="text-[10px] font-medium text-[#1A1A2E]">
                      {new Date(activeConversation.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-[#6B7280]">Last Activity</span>
                    <span className="text-[10px] font-medium text-[#1A1A2E]">
                      {timeAgo(activeConversation.lastMsgAt || activeConversation.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] text-[#6B7280]">Messages</span>
                    <span className="text-[10px] font-medium text-[#1A1A2E]">
                      {messages.length}
                    </span>
                  </div>
                  {activeConversation.assignedTo && (
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-[#6B7280]">Assigned</span>
                      <span className="text-[10px] font-medium text-[#1A1A2E] flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Admin
                      </span>
                    </div>
                  )}
                  {activeConversation.closedAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-[#6B7280]">Closed</span>
                      <span className="text-[10px] font-medium text-[#1A1A2E]">
                        {new Date(activeConversation.closedAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#6B7280]">Conversation not found</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
